import { BASE_URL } from './constants';
import type {
  Customer,
  DeliveryAddress,
  ExcludedOrderReason,
  ProblemDetail,
  ReceiptInbox,
  ReceiptInboxAddress,
  RouteProduct,
  RouteStop,
  Subscription,
  SubscriptionType,
  TruckRoute,
  Weekday
} from '@/types/types';
import { WEEKDAYS } from '@/types/types';
import { HttpStatusCode, isAxiosError } from 'axios';
import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Registered here (not in App.tsx) so this module's own exports work regardless of import order —
// a file that imports only formatInstantTime (e.g. a unit test) must not depend on App.tsx having
// run first. dayjs.extend is a no-op on a plugin already installed.
dayjs.extend(utc);
dayjs.extend(timezone);

export function getImageUrl(name: string, type = 'jpg') {
  return new URL(`src/assets/img/${name}.${type}`, BASE_URL).href;
}

const DATE_FORMAT = 'DD/MM/YYYY';

export const formatDate = (date: Dayjs) => {
  return `${date.format(DATE_FORMAT)}`;
};
export const formatDateWithDay = (date: Dayjs) => {
  const dayOfWeek = date.format('dddd');
  return `${dayOfWeek} - ${date.format(DATE_FORMAT)}`;
};

// REQ-5: shared by the Receipt Inbox list and its pick-address dialog — one of these is each line
// a staff member picks from when an item's linked customer has more than one.
export const formatReceiptInboxAddress = (address: ReceiptInboxAddress): string =>
  `${address.street}, ${address.city} ${address.postalCode}`;

// Shared by the Receipt Inbox list and its confirm/reject/pick-address dialogs — never the raw
// customerId UUID, and never silently blank for the one state (unknown customer) where there is
// no name to show.
export const formatReceiptInboxCustomerName = (review: ReceiptInbox): string =>
  review.customerName ?? 'Unknown customer';

export const formatCustomerName = (customer: {
  firstName: string;
  lastName: string | null;
  invoiceAccount?: { companyName: string } | null;
}) => {
  const customerName = `${customer.firstName} ${customer.lastName || ''}`.trim();
  if (customer.invoiceAccount) {
    return `${customerName} (${customer.invoiceAccount.companyName})`;
  }
  return customerName;
};

export type OrderCustomerOption = {
  id: string;
  name: string;
  deliveryAddresses: DeliveryAddress[];
  // REQ-27: carried so CreateOrderForm can show the renewal warning from the picked option alone,
  // without needing the full (now server-searched, not bulk-loaded) customer list it came from.
  subscriptions: Subscription[];
};

/** The customer dropdown's options for order creation: name A-Z, each with its own addresses. */
export const buildOrderCustomerOptions = (
  customers: Customer[] | undefined
): OrderCustomerOption[] =>
  (customers ?? [])
    .slice()
    .sort((a, b) => a.firstName.toLocaleLowerCase().localeCompare(b.firstName.toLocaleLowerCase()))
    .map(customer => ({
      id: customer.id,
      name: `${customer.firstName}${customer.lastName ? ` ${customer.lastName}` : ''}`,
      deliveryAddresses: customer.deliveryAddresses,
      subscriptions: customer.subscriptions
    }));

/** A customer with exactly one delivery address auto-selects it; more than one needs a choice. */
export const findOnlyDeliveryAddress = (
  deliveryAddresses: DeliveryAddress[] | undefined
): DeliveryAddress | undefined =>
  deliveryAddresses?.length === 1 ? deliveryAddresses[0] : undefined;

/**
 * Get human-readable label for subscription type
 */
export const getSubscriptionTypeLabel = (type: SubscriptionType): string => {
  const labels: Record<SubscriptionType, string> = {
    E_MAX: 'E-Max',
    ELEGANCE: 'Elegance',
    WATER_ONLY: 'Water Only',
    SHORT_RENTAL: 'Short-Term Rental'
  };
  return labels[type] || type;
};

/**
 * REQ-27: the subscriptions across a customer's delivery addresses that are due for renewal
 * (ended, or ending within 30 days) -- `renewalDue` is computed by apps/api
 * (apps/api/README.md § Subscription Management) from the server's own clock; this never
 * recomputes that rule, only collects what the API already flagged. REQ-5: subscriptions are
 * customer-level now, not nested per address, so the caller passes the flat list directly.
 */
export const getDueSubscriptions = (subscriptions: Subscription[]): Subscription[] =>
  subscriptions.filter(s => s.renewalDue);

/**
 * REQ-9: whether an error is the version-conflict 409 apps/api answers when an update's `version`
 * is stale (apps/api/README.md § API Conventions) -- the one place every version-checked mutation
 * hook (use-customers.ts, use-orders.ts) tests for it, so "someone else changed this record" is
 * recognized the same way everywhere.
 */
export const isConflictError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === HttpStatusCode.Conflict;

/**
 * Whether an error is the 404 apps/api answers for a record that is gone, such as an entry another
 * person deleted while this screen was open.
 */
export const isNotFoundError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound;

/**
 * react-base/RULES.md § Error severity: any HTTP 4xx is an expected refusal (an amber warning),
 * anything else, a 5xx, a network failure or an error the client cannot classify, is a fault (a
 * red error).
 */
export const isExpectedApiError = (error: unknown): boolean => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500;
};

/**
 * REQ-9: the one "someone else changed this" wording every version-checked form shows on a 409 —
 * ContactInformation, InvoiceAccount, DeliveryAddresses, UpdateOrderForm,
 * EditSubscriptionEndDateDialog and the knowledge base entry dialog each wrote this same sentence
 * with only the entity name different.
 */
export const conflictMessage = (
  entity: 'customer' | 'order' | 'subscription' | 'knowledge base entry' | 'payment method'
): string =>
  `Someone else changed this ${entity}. Reloading the latest version — your changes are kept, try saving again.`;

/**
 * Get category label for subscription type
 */
export const getSubscriptionCategory = (
  type: SubscriptionType
): 'yearly' | 'water_only' | 'short_term' => {
  if (type === 'WATER_ONLY') return 'water_only';
  if (type === 'SHORT_RENTAL') return 'short_term';
  return 'yearly';
};

const WEEKDAY_LABELS: Record<Weekday, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday'
};

export const getWeekdayLabel = (weekday: Weekday): string => WEEKDAY_LABELS[weekday];

/**
 * The API stores weekdays as a `Set`, so the order Jackson serializes them in is not guaranteed
 * to be Monday-first. Sort before display so a group's days always read Mon -> Fri.
 */
export const sortWeekdays = (weekdays: Weekday[]): Weekday[] =>
  [...weekdays].sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b));

export const formatWeekdays = (weekdays: Weekday[]): string =>
  sortWeekdays(weekdays).map(getWeekdayLabel).join(', ');

// `Dayjs#day()` returns 0 (Sunday) to 6 (Saturday); indices 0 and 6 map to `null` since a
// delivery group's own weekdays (WEEKDAYS, types.ts) never include a weekend day.
const WEEKDAY_BY_DAY_INDEX: (Weekday | null)[] = [
  null,
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  null
];

/**
 * REQ-39: whether `date` falls on a day of the week a group's own `weekdays` does not cover --
 * the order form's "deliver on this day anyway" tick shows only then (docs/backlog/m14.md
 * Planning notes: "The tick lifts only the weekday rule"). A weekend date is always a non-delivery
 * day, since no group ever delivers one.
 */
export const isNonDeliveryDay = (date: Dayjs, groupWeekdays: Weekday[]): boolean => {
  const weekday = WEEKDAY_BY_DAY_INDEX[date.day()] ?? null;
  return weekday === null || !groupWeekdays.includes(weekday);
};

/**
 * REQ-39: whether the order form's "deliver on this day anyway" tick should show at all --
 * only once a delivery date is picked and the address's own group is known (`groupWeekdays`
 * undefined means neither yet applies), and that date turns out to be a non-delivery day for it.
 * Shared by use-create-order-form.ts and use-update-order-form.ts, which otherwise each derived
 * this the same way.
 */
export const shouldShowDeliverAnyway = (
  deliveryDate: string | undefined,
  groupWeekdays: Weekday[] | undefined
): boolean =>
  !!deliveryDate && !!groupWeekdays && isNonDeliveryDay(dayjs(deliveryDate), groupWeekdays);

// REQ-14: the one place that reads apps/api's RFC 7807 problem-detail body (every error response —
// react-base/RULES.md § API client's "typed ApiError normalised from one error shape") —
// getApiErrorMessage and getApiFieldErrors below are its only two consumers, so a form or a toast
// never reaches into `error.response.data` on its own.
const parseProblemDetail = (error: unknown): ProblemDetail | undefined =>
  isAxiosError<ProblemDetail>(error) ? error.response?.data : undefined;

/**
 * Narrows an unknown mutation (or query) error down to a message safe to show in a toast or an
 * inline error: the problem detail's own `detail`, falling back to its `title` (the unauthenticated
 * 404 AuthenticationFilter hand-builds carries no `detail` — a missing field, not a parse failure),
 * else the caller's own fallback. Never `catch (e: any)` — see react-base/RULES.md § Error handling.
 *
 * An `AxiosError`'s own `.message` (e.g. "Request failed with status code 500") is never used as
 * a middle fallback here, even though `AxiosError extends Error`: it is axios's own generic,
 * always-present transport-level message, not anything the API said, so it is never worth
 * showing over the caller's own, purpose-written fallback. Skipping straight to `fallback` for
 * any axios error with no parseable problem detail is the fix — the previous `error instanceof
 * Error && error.message` branch below used to catch every such case first, since that condition
 * is true for every `AxiosError`, which silently discarded every caller's fallback text whenever
 * the API response carried no body the parser could read.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (isAxiosError<ProblemDetail>(error)) {
    const problem = parseProblemDetail(error);
    return problem?.detail || problem?.title || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

/**
 * REQ-14/REQ-19: narrows a mutation error down to the per-field messages a 400 bean-validation
 * failure carries (`ProblemDetail.fields`, e.g. a delivery address saved without
 * latitude/longitude), keyed by field name so a form can pass a field's own message straight to
 * its `helperText` or react-hook-form's `setError`. Any other error shape — network, 404, a
 * detail-only 400 — yields `{}`, which a form treats the same as "no field-level error to show".
 */
export const getApiFieldErrors = (error: unknown): Record<string, string> =>
  parseProblemDetail(error)?.fields ?? {};

/**
 * REQ-14: for a mutation with no visible per-field slot to attach `getApiFieldErrors` to (e.g.
 * CustomerSteps — the rejected field lives on an earlier wizard step than the one Finish is
 * pressed from), appends the field messages to the toast instead, so a bean-validation rejection
 * is never reduced to the generic "Validation failed" with no indication of which field or why.
 * Falls back to `getApiErrorMessage` alone when there are no field errors to append.
 */
export const getApiErrorToastMessage = (error: unknown, fallback: string): string => {
  const fieldMessages = Object.values(getApiFieldErrors(error));
  const detail = getApiErrorMessage(error, fallback);
  return fieldMessages.length > 0 ? `${detail}: ${fieldMessages.join(' ')}` : detail;
};

/**
 * The customer-update endpoint returns the whole Customer, id included, but never names which
 * delivery address it just created (REQ-3's "create an address and assign its group in the same
 * flow" needs that id to call the group-assignment endpoint next). Diffs the address lists from
 * immediately before and after the save to find it.
 */
export const findNewAddressId = (
  before: DeliveryAddress[],
  after: DeliveryAddress[]
): string | undefined => {
  const beforeIds = new Set(before.map(address => address.id));
  return after.find(address => !beforeIds.has(address.id))?.id;
};

/**
 * REQ-5: planning settings' time fields cross the API as a bare `LocalTime` ("16:00:00", no date
 * component) — see react-base/RULES.md § Dates & time's ISO-8601 exception for wall-clock-only
 * values. Split on `:` rather than parsing with a format string: dayjs needs the
 * `customParseFormat` plugin for the latter, which nothing else in this app registers.
 */
export const parseTimeOfDay = (time: string): Dayjs => {
  const [hour = 0, minute = 0] = time.split(':').map(Number);
  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
};

/** The inverse of parseTimeOfDay: always writes back with seconds, matching `LocalTime`'s
 * "HH:mm:ss" serialization (apps/api's `PlanningSettingsResponse`). */
export const formatTimeOfDay = (date: Dayjs): string => `${date.format('HH:mm')}:00`;

// Exported so a module that needs the same business timezone for its own calendar-day logic
// (agent-log-date-range.ts) reads it from here rather than repeating the literal.
export const MADRID_TIMEZONE = 'Europe/Madrid';

/**
 * REQ-14/REQ-15: every route timestamp (`estimatedArrival`, `estimatedFinishTime`, `generatedAt`)
 * crosses the API as an ISO-8601 UTC instant (react-base/RULES.md § API client) and is shown to
 * staff in Europe/Madrid, the only timezone this app's drivers and trucks operate in.
 */
export const formatInstantTime = (instant: string): string =>
  dayjs(instant).tz(MADRID_TIMEZONE).format('HH:mm');

/** Same as formatInstantTime, plus the day — the route sheet's header ("generated at ..."). */
export const formatInstantDateTime = (instant: string): string =>
  dayjs(instant).tz(MADRID_TIMEZONE).format('DD/MM/YYYY HH:mm');

/** REQ-14/REQ-15: a route's truck name is null when the truck was deleted after generating. */
export const getRouteTruckLabel = (truckRoute: TruckRoute): string =>
  truckRoute.truckName ?? 'Deleted truck';

/** REQ-14: a route's driver line; a driver deleted after generating keeps its id, not its name. */
export const getRouteDriverLabel = (truckRoute: TruckRoute): string => {
  if (truckRoute.driverId === null) return 'No driver assigned today';
  return `Driver: ${truckRoute.driverName ?? 'deleted driver'}`;
};

const EXCLUDED_ORDER_REASON_LABELS: Record<ExcludedOrderReason, string> = {
  LATE: 'Registered after the cutoff',
  // REQ-38 (M14): narrowed to mean only an address in no delivery group at all -- a group that
  // simply does not deliver this weekday is its own reason below now.
  UNASSIGNED_ADDRESS: 'No delivery group',
  NOT_A_DELIVERY_DAY: 'Group does not deliver this day',
  NO_COORDINATES: 'Delivery address has no coordinates',
  BOTTLE_VALUE_MISSING: 'An item has no bottle value set',
  NO_ROOM: 'No room on any truck',
  // REQ-10: an agent-created order staff have not approved yet (apps/ui/README.md § Agent Orders).
  AWAITING_REVIEW: 'Awaiting review'
};

/** REQ-8: the not-on-route list's human-readable reason. */
export const getExcludedOrderReasonLabel = (reason: ExcludedOrderReason): string =>
  EXCLUDED_ORDER_REASON_LABELS[reason];

/**
 * REQ-15: one product line's display text — the truck-route card, the not-on-route list and the
 * printable sheet all show the same "item × quantity — N bottles" line for a stop or an excluded
 * order's products; `bottles: null` means the item has no stored bottle value yet (REQ-7: flagged,
 * never counted as zero).
 */
export const formatRouteProductLine = (product: RouteProduct): string =>
  `${product.itemName} × ${product.quantity} — ${
    product.bottles === null ? 'unknown bottles' : `${product.bottles} bottles`
  }`;

/**
 * REQ-15/REQ-28: a route stop's customer-facing label, shared by the truck-route card and the
 * route sheets PDF — a delivery names its customer, a reload names the depot, and a break names
 * itself.
 */
export const describeRouteStop = (stop: RouteStop): string => {
  if (stop.type === 'DELIVERY') return stop.customerName ?? '';
  if (stop.type === 'RELOAD') return 'Return to depot';
  return stop.label ?? '';
};

/**
 * REQ-15/REQ-28: a stop's product lines for the route sheets PDF — a break shows its minutes
 * instead, a reload shows none, a delivery shows one `formatRouteProductLine` per product.
 */
export const describeRouteStopProducts = (stop: RouteStop): string[] => {
  if (stop.type === 'BREAK') return stop.minutes === null ? [] : [`${stop.minutes} min`];
  if (stop.type === 'RELOAD' || !stop.products) return [];
  return stop.products.map(formatRouteProductLine);
};

/**
 * The bottles a truck must carry for its route: the sum of its delivery stops' totals, so staff
 * know what to load. `complete: false` means at least one delivery's total is unknown (an item with
 * no stored bottle value, REQ-7), so `bottles` is only what is known — a lower bound, never
 * presented as the whole count.
 */
export const getTruckRouteBottles = (
  truckRoute: TruckRoute
): { bottles: number; complete: boolean } => {
  let bottles = 0;
  let complete = true;
  for (const stop of truckRoute.stops) {
    if (stop.type !== 'DELIVERY') continue;
    if (stop.totalBottles === null) complete = false;
    else bottles += stop.totalBottles;
  }
  return { bottles, complete };
};

/** The truck-route card's and the route sheets PDF's one-line bottle total for a truck. */
export const formatTruckRouteBottles = (truckRoute: TruckRoute): string => {
  const { bottles, complete } = getTruckRouteBottles(truckRoute);
  return complete
    ? `Total bottles in truck: ${bottles}`
    : `Total bottles in truck: at least ${bottles} (an item has no bottle value)`;
};

/**
 * REQ-28: triggers a browser download of an already-generated blob — generic enough that nothing
 * here needs to know it is a PDF. A plain `<a download>` created and clicked once, never left in
 * the DOM, so the trigger stays one real `<button>` rather than a `<button>` nested inside an
 * `<a>` (`PDFDownloadLink`'s own rendering).
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
